import { useEffect, useRef } from 'react'
import useHashSignal from 'hooks/useHashSignal'

/**
 * Opens a modal when `hash` is in the URL, then clears the hash when the modal
 * finishes — the same pattern as `#societe-rewards` on Maldives Direct.
 */
function useOpenModalOnHash(
  hash: `#${string}`,
  onActivate: () => void | Promise<unknown>,
  enabled = true,
): void {
  const inFlightRef = useRef(false)
  const cancelledRef = useRef(false)

  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const { clearHash } = useHashSignal({
    hash,
    enabled,
    onActivate: async() => {
      // A second false→true transition (e.g. login then account settle) must
      // not start another open while the first is still awaiting login/data.
      if (inFlightRef.current || cancelledRef.current) return
      inFlightRef.current = true
      try {
        await onActivate()
      } finally {
        inFlightRef.current = false
        // Skip if we unmounted or the modal already navigated away — otherwise
        // a stale replace() would send the user back to the landing page.
        if (!cancelledRef.current && window.location.hash === hash) {
          clearHash()
        }
      }
    },
  })
}

export default useOpenModalOnHash
